import {
  type StackCompound,
  type FrequencyKey,
  type TierKey,
  FREQ_PER_WEEK,
  TIER_NAMES,
  PRESETS,
} from '../data/peptidePresets';

// === Patient Metrics ===

export interface PatientMetrics {
  sex: 'male' | 'female';
  age: number;
  weightKg: number;
  weightLbs: number;
  heightCm: number;
  heightIn: number;
  bmi: number;
  bsa: number;
  ibw: number;
}

export function lbsToKg(lbs: number): number {
  return lbs * 0.453592;
}

export function kgToLbs(kg: number): number {
  return kg * 2.20462;
}

export function inToCm(inches: number): number {
  return inches * 2.54;
}

export function cmToIn(cm: number): number {
  return cm / 2.54;
}

export function getPatientMetrics(
  sex: 'male' | 'female',
  age: number,
  weight: number,
  weightUnit: 'lbs' | 'kg',
  height: number,
  heightUnit: 'in' | 'cm'
): PatientMetrics {
  const weightKg = weightUnit === 'kg' ? weight : lbsToKg(weight);
  const weightLbs = weightUnit === 'lbs' ? weight : kgToLbs(weight);
  const heightCm = heightUnit === 'cm' ? height : inToCm(height);
  const heightIn = heightUnit === 'in' ? height : cmToIn(height);
  const heightM = heightCm / 100;

  const bmi = weightKg / (heightM * heightM);
  const bsa = Math.sqrt((heightCm * weightKg) / 3600);
  const ibw = sex === 'male'
    ? 50 + 2.3 * (heightIn - 60)
    : 45.5 + 2.3 * (heightIn - 60);

  return {
    sex, age,
    weightKg, weightLbs,
    heightCm, heightIn,
    bmi, bsa,
    ibw: Math.max(ibw, 0),
  };
}

// === Dose Math ===

export interface DoseResult {
  doseMg: number;
  concMg: number;
  vol: number;       // mL to draw
  units: number;     // insulin units (U-100)
  totalMg: number;   // total mg in vial
  dpv: number;       // doses per vial
  pw: number;        // injections per week
  wkMg: number;      // mg per week
  wpv: number;       // weeks per vial
}

export function calcCompound(c: {
  dose: number;
  doseUnit: string;
  conc: number;
  concUnit: string;
  vial: number;
  freq: FrequencyKey;
}): DoseResult {
  const doseMg = c.doseUnit === 'mcg' ? c.dose / 1000 : c.dose;
  const concMg = c.concUnit === 'mcg/mL' ? c.conc / 1000 : c.conc;
  const vol = doseMg / concMg;
  const units = vol * 100;
  const totalMg = concMg * c.vial;
  const dpv = totalMg / doseMg;
  const pw = FREQ_PER_WEEK[c.freq] || 1;
  const wkMg = doseMg * pw;
  const wpv = dpv / pw;

  return { doseMg, concMg, vol, units, totalMg, dpv, pw, wkMg, wpv };
}

export function fmtMg(v: number): string {
  return v >= 0.01 ? v.toFixed(v < 1 ? 3 : 2) : v.toFixed(4);
}

// === Stack Assessment ===

export function generateAssessment(
  pt: PatientMetrics,
  stack: StackCompound[]
): string[] {
  if (!stack.length) return ['Add compounds to see assessment.'];

  const lines: string[] = [];
  const names = stack.map(c => c.name);
  const cats = [...new Set(stack.map(c => {
    const pk = Object.keys(PRESETS).find(k => PRESETS[k].name === c.name);
    return pk ? PRESETS[pk].category : 'unknown';
  }))];
  const highT = stack.filter(c => c.tier === 'high' || c.tier === 'veryhigh');
  const cat2 = stack.filter(c => {
    const pk = Object.keys(PRESETS).find(k => PRESETS[k].name === c.name);
    return pk && PRESETS[pk].regulatory === 'cat2';
  });
  const inv = stack.filter(c => {
    const pk = Object.keys(PRESETS).find(k => PRESETS[k].name === c.name);
    return pk && PRESETS[pk].regulatory === 'investigational';
  });

  const bmiCat = pt.bmi < 18.5 ? 'underweight' : pt.bmi < 25 ? 'normal' : pt.bmi < 30 ? 'overweight' : 'obese';
  lines.push(`<strong>Patient:</strong> ${pt.sex === 'male' ? 'Male' : 'Female'}, ${pt.age}y, ${pt.weightKg.toFixed(1)} kg (BMI ${pt.bmi.toFixed(1)} — ${bmiCat}), BSA ${pt.bsa.toFixed(2)} m².`);
  lines.push('');
  lines.push(`<strong>Stack:</strong> ${stack.length} compound${stack.length > 1 ? 's' : ''} across ${cats.length} categor${cats.length > 1 ? 'ies' : 'y'} (${cats.map(c => c[0].toUpperCase() + c.slice(1)).join(', ')}).`);

  if (cats.length >= 4) {
    lines.push('⚠️ Broad multi-category stack. Consider phasing — introduce compounds sequentially to isolate effects.');
  }
  lines.push('');

  // GLP-1 conflict check
  const glp1s = names.filter(n => ['Semaglutide', 'Tirzepatide', 'Retatrutide'].includes(n));
  if (glp1s.length > 1) {
    lines.push(`<strong style="color:var(--error);">⚠️ GLP-1 Conflict:</strong> ${glp1s.join(' + ')} selected. Do NOT combine GLP-1 agonists — overlapping mechanism, additive GI AEs. Choose one.`);
    lines.push('');
  }

  // GH synergy
  const ghC = names.filter(n => ['Sermorelin', 'Tesamorelin', 'Ipamorelin'].includes(n));
  if (ghC.length >= 2) {
    if (names.includes('Sermorelin') && names.includes('Ipamorelin')) {
      lines.push('<strong>GH Synergy:</strong> Sermorelin (GHRH) + Ipamorelin (GHRP) — well-established synergistic pairing. Consider reducing Ipamorelin to 100–150 mcg when co-administered. Monitor IGF-1 at 4–6 wk.');
    }
    if (names.includes('Sermorelin') && names.includes('Tesamorelin')) {
      lines.push('<strong>Note:</strong> Both are GHRH analogs — typically alternated, not co-administered. Pick based on goal (Tesamorelin → visceral fat, Sermorelin → broad GH).');
    }
    lines.push('');
  }

  // Healing stack
  if (names.includes('BPC-157') && names.includes('TB-500')) {
    lines.push('<strong>Healing Stack:</strong> BPC-157 + TB-500 — gold-standard (local + systemic). Both FDA Cat 2 — sourcing limited. Note BPC-157 bell-shaped curve; more ≠ better.');
    lines.push('');
  }

  // Dosing intensity
  if (highT.length) {
    lines.push(`<strong>Dosing Intensity:</strong> ${highT.length} compound${highT.length > 1 ? 's' : ''} at high/very-high (${highT.map(c => `${c.name}: ${TIER_NAMES[c.tier as TierKey] || 'Custom'}`).join(', ')}).`);
    if (highT.length > 2) {
      lines.push('Running 3+ compounds at aggressive tiers increases cumulative AE burden. Consider stepping one down to standard and titrating individually.');
    }
    lines.push('');
  }

  // Regulatory warnings
  if (cat2.length) {
    lines.push(`<strong style="color:var(--error);">Regulatory:</strong> ${cat2.map(c => c.name).join(', ')} — FDA Cat 2 (prohibited from compounding). Sourcing from research suppliers only. QA/sterility concerns.`);
    lines.push('');
  }
  if (inv.length) {
    lines.push(`<strong style="color:var(--warning);">Investigational:</strong> ${inv.map(c => c.name).join(', ')} — not FDA-approved. Clinical trials or research sourcing only.`);
    lines.push('');
  }

  // BMI context
  if (pt.bmi >= 30 && glp1s.length) {
    lines.push(`<strong>Weight Context:</strong> BMI ${pt.bmi.toFixed(1)} supports GLP-1 RA indication. Titration-based (not weight-adjusted). Target 5% for metabolic benefit, 10%+ for CV risk reduction.`);
    lines.push('');
  }

  // Age context
  if (pt.age > 55 && ghC.length) {
    lines.push(`<strong>Age Note:</strong> At ${pt.age}y, GH secretagogue response may be blunted. Consider upper dosing range for ${ghC.join('/')} and more frequent IGF-1 monitoring.`);
    lines.push('');
  }

  // Injection burden
  let totalInj = 0;
  stack.forEach(c => { totalInj += FREQ_PER_WEEK[c.freq] || 1; });
  if (totalInj > 14) {
    lines.push(`<strong>Injection Burden:</strong> ${totalInj} inj/wk. Consolidate timing (bedtime fasted for GH, morning for metabolic). Rotate sites.`);
    lines.push('');
  }

  // Labs recommendation
  const labs = ['CBC', 'CMP', 'lipid panel'];
  if (ghC.length) labs.push('IGF-1');
  if (glp1s.length) labs.push('HbA1c', 'lipase');
  if (names.includes('GHK-Cu')) labs.push('copper', 'ceruloplasmin');
  lines.push(`<strong>Recommended Baseline Labs:</strong> ${[...new Set(labs)].join(', ')}.`);

  return lines.filter(l => l !== undefined);
}
